import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchRiskScenarios } from "../../redux/actions/riskScenariosActionCreators";
import { fetchEvaluationList } from "../../redux/actions/evaluationAction";
import {
  fetchRiskAssessment,
  updateRiskAssessmentHypotheses,
} from "../../redux/actions/riskAssessmentActionCreators";
import { alertError } from "../../utils/feedback";

const CATEGORY_ORDER = ["worst", "median", "best"];
const CATEGORY_LABELS = {
  worst: "Worst Case",
  median: "Median Case",
  best: "Best Case",
};

const CATEGORY_KEYS = {
  worst: ["worst_case", "worst"],
  median: ["median_case", "median"],
  best: ["best_case", "best"],
};

const normalizeStatus = (rawValue) => {
    const value = String(rawValue ?? "").trim().toLowerCase();
    if (value === "in_progress") {
      return "in_progress";
    }
    if (value === "not_started") {
      return "not_started";
    }
    return value;
  };

const toStringList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item == null ? "" : String(item).trim()))
    .filter(Boolean);
};

const toAssumptionItems = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const title = String(item.title || "").trim();
        if (!title) return null;
        const usecaseId =
          item.id != null && String(item.id).trim()
            ? String(item.id).trim()
            : item.usecase_id != null && String(item.usecase_id).trim()
            ? String(item.usecase_id).trim()
            : null;
        return {
          id: usecaseId,
          title,
          metrics: toStringList(item.metrics),
        };
      }
      const title = String(item ?? "").trim();
      if (!title) return null;
      return { id: null, title, metrics: [] };
    })
    .filter(Boolean);
};

const getScenarioAssumptionsByCategory = (scenario, category) => {
  const assumptionsObj =
    scenario?.assumptions && typeof scenario.assumptions === "object"
      ? scenario.assumptions
      : scenario?.hypotheses && typeof scenario.hypotheses === "object"
      ? scenario.hypotheses
      : {};

  const keys = CATEGORY_KEYS[category] || [category];
  const candidates = [
    ...keys.map((k) => assumptionsObj?.[k]),
    ...keys.map((k) => scenario?.[k]),
    scenario?.[`${category}_hypotheses`],
    scenario?.[`${category}_assumptions`],
    scenario?.[`${category}Hypotheses`],
    scenario?.[`${category}Assumptions`],
  ];

  for (const candidate of candidates) {
    const items = toAssumptionItems(candidate);
    if (items.length > 0) return items;
  }

  return [];
};

const buildDefaultItem = (category, scenarioTitle) => {
  if (category === "worst") {
    return { title: `Worst-case hypothesis for ${scenarioTitle}`, metrics: [] };
  }
  if (category === "median") {
    return { title: `Median hypothesis for ${scenarioTitle}`, metrics: [] };
  }
  return { title: `Best-case hypothesis for ${scenarioTitle}`, metrics: [] };
};

const collectEditedItems = (currentRows, baselineRows) => {
  const changes = [];
  const safeRows = currentRows && typeof currentRows === "object" ? currentRows : {};

  Object.keys(safeRows).forEach((scenarioId) => {
    CATEGORY_ORDER.forEach((category) => {
      const currentItems = Array.isArray(safeRows?.[scenarioId]?.[category])
        ? safeRows[scenarioId][category]
        : [];

      currentItems.forEach((item, index) => {
        const currentTitle = String(item?.title || "").trim();
        const initialTitle = String(
          baselineRows?.[scenarioId]?.[category]?.[index]?.title || ""
        ).trim();

        if (currentTitle !== initialTitle) {
          const usecaseIdRaw =
            currentRows?.[scenarioId]?.[category]?.[index]?.id ??
            baselineRows?.[scenarioId]?.[category]?.[index]?.id ??
            null;
          const usecaseId =
            usecaseIdRaw != null && String(usecaseIdRaw).trim()
              ? String(usecaseIdRaw).trim()
              : null;

          changes.push({
            scenarioId,
            usecaseId,
            category,
            index,
            before: initialTitle,
            after: currentTitle,
          });
        }
      });
    });
  });

  return changes;
};

export default function RiskAssessment() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { orgaId: orgaIdFromUrl } = useParams();
  const theme = useTheme();
  const orgaIdFromState = useSelector((state) => state.organizationProfile?.id);
  const orgaId = String(
    orgaIdFromUrl || orgaIdFromState || localStorage.getItem("orgaId") || ""
  ).trim();

  const orgaProfile =
    useSelector((state) => state.organizationProfile?.organizationMetricsById) ||
    {};
  const evaluationList = useSelector(
    (state) => state.evaluation?.evaluations ?? []
  );
  const riskScenariosData = useSelector(
    (state) => state.riskScenarios?.risk_scenarios
  );
  const riskAssessmentData = useSelector((state) => state.riskAssessment || {});

  const [rowsByScenario, setRowsByScenario] = useState({});
  const [initialRowsByScenario, setInitialRowsByScenario] = useState({});
  const [errors, setErrors] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const [showMoreByKey, setShowMoreByKey] = useState({});
  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const textMeasureRefs = useRef({});

  const riskAssessmentPhase = evaluationList.find(
    (phase) => phase.phase === "risk_assesment"
  );
  const validationEnabled =
    typeof riskAssessmentPhase?.validation_status === "boolean"
      ? riskAssessmentPhase.validation_status
      : typeof orgaProfile?.validation_status === "boolean"
      ? orgaProfile.validation_status
      : true;
  const riskAssessmentStatus = normalizeStatus(
    riskAssessmentPhase?.status || orgaProfile?.risk_assesment_status
  );
  const riskAssessmentValidated =
    riskAssessmentPhase?.user_validated != null
      ? Boolean(riskAssessmentPhase.user_validated)
      : Boolean(orgaProfile?.risk_assesment_validation);
  const activeEditMode =
    validationEnabled &&
    ((riskAssessmentStatus === "completed" && !riskAssessmentValidated) ||
      location.state?.editMode === true);

  const selectedScenariosFromBackend = useMemo(() => {
    const normalizedRiskScenarios = Array.isArray(riskScenariosData)
      ? riskScenariosData
      : [];

    const selected = normalizedRiskScenarios
      .filter((scenario) => String(scenario?.status || "").toLowerCase() === "selected")
      .slice(0, 5);
    if (selected.length > 0) return selected;

    return normalizedRiskScenarios
      .filter((scenario) => scenario?.recommended)
      .slice(0, 5);
  }, [riskScenariosData]);

  const mappedHypothesesScenarios = useMemo(() => {
    const rawHypotheses = Array.isArray(riskAssessmentData?.hypotheses)
      ? riskAssessmentData.hypotheses
      : [];

    return rawHypotheses.slice(0, 5).map((hypothesis, index) => {
      const scenarioId = String(
        hypothesis?.scenario_id || `hypothesis-${index + 1}`
      );
      const scenarioTitle = String(
        hypothesis?.scenario_title || hypothesis?.scenario_titles || ""
      ).trim();
      const associatedCases =
        hypothesis?.associated_cases &&
        typeof hypothesis.associated_cases === "object" &&
        !Array.isArray(hypothesis.associated_cases)
          ? hypothesis.associated_cases
          : {};

      return {
        id: scenarioId,
        title: scenarioTitle || `Scenario ${index + 1}`,
        assumptions: {
          worst_case: Array.isArray(associatedCases?.worst_case)
            ? associatedCases.worst_case
            : [],
          median_case: Array.isArray(associatedCases?.median_case)
            ? associatedCases.median_case
            : [],
          best_case: Array.isArray(associatedCases?.best_case)
            ? associatedCases.best_case
            : [],
        },
        status: "selected",
      };
    });
  }, [riskAssessmentData]);

  const displayScenarios = useMemo(() => {
    if (mappedHypothesesScenarios.length > 0) {
      return mappedHypothesesScenarios;
    }
    return selectedScenariosFromBackend;
  }, [mappedHypothesesScenarios, selectedScenariosFromBackend]);

  const selectedScenario = useMemo(() => {
    if (displayScenarios.length === 0) return null;
    return (
      displayScenarios.find(
        (scenario) => String(scenario?.id || "") === selectedScenarioId
      ) || displayScenarios[0]
    );
  }, [displayScenarios, selectedScenarioId]);

  const activeScenarioId = String(selectedScenario?.id || "");
  const activeScenarioRow = activeScenarioId
    ? rowsByScenario?.[activeScenarioId] || {}
    : {};
  const activeScenarioHypothesesCount = CATEGORY_ORDER.reduce((sum, category) => {
    const list = Array.isArray(activeScenarioRow?.[category])
      ? activeScenarioRow[category]
      : [];
    return sum + list.length;
  }, 0);

  useEffect(() => {
    if (!orgaId) return;
    dispatch(fetchRiskScenarios(orgaId));
    dispatch(fetchEvaluationList(orgaId));
    dispatch(fetchRiskAssessment(orgaId));
  }, [dispatch, orgaId]);

  useEffect(() => {
    if (displayScenarios.length === 0) {
      setSelectedScenarioId("");
      return;
    }

    setSelectedScenarioId((current) => {
      const hasCurrent = displayScenarios.some(
        (scenario) => String(scenario?.id || "") === current
      );
      if (hasCurrent) return current;
      return String(displayScenarios[0]?.id || "");
    });
  }, [displayScenarios]);

  useEffect(() => {
    const next = {};
    for (const scenario of displayScenarios) {
      const scenarioId = String(scenario?.id || "");
      if (!scenarioId) continue;

      const byCategory = {};
      for (const category of CATEGORY_ORDER) {
        const fromScenario = getScenarioAssumptionsByCategory(scenario, category);
        const fallback = [
          buildDefaultItem(category, String(scenario?.title || "this scenario")),
        ];
        byCategory[category] =
          fromScenario.length > 0
            ? fromScenario
            : fallback;
      }

      next[scenarioId] = byCategory;
    }

    const baseline = JSON.parse(JSON.stringify(next));
    setRowsByScenario(next);
    setInitialRowsByScenario(baseline);
    setErrors({});
    setEditingItem(null);
    setEditingValue("");
    setExpandedItems({});
    setShowMoreByKey({});
  }, [displayScenarios]);

  useEffect(() => {
    const updateShowMoreState = () => {
      const next = {};
      Object.entries(textMeasureRefs.current).forEach(([itemKey, node]) => {
        if (!node) return;
        const computed = window.getComputedStyle(node);
        const lineHeight = parseFloat(computed.lineHeight || "0");
        if (!lineHeight || Number.isNaN(lineHeight)) return;
        const lineCount = Math.ceil(node.scrollHeight / lineHeight);
        next[itemKey] = lineCount > 3;
      });
      setShowMoreByKey(next);
    };

    const timer = window.setTimeout(updateShowMoreState, 0);
    window.addEventListener("resize", updateShowMoreState);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", updateShowMoreState);
    };
  }, [
    rowsByScenario,
    displayScenarios,
    activeEditMode,
    expandedItems,
    activeScenarioId,
  ]);

  const updateHypothesisValue = (scenarioId, category, index, value) => {
    setRowsByScenario((prev) => {
      const next = { ...prev };
      const scenarioRows = { ...(next[scenarioId] || {}) };
      const categoryRows = Array.isArray(scenarioRows[category])
        ? [...scenarioRows[category]]
        : [];
      const current =
        categoryRows[index] && typeof categoryRows[index] === "object"
          ? categoryRows[index]
          : { id: null, title: "", metrics: [] };
      categoryRows[index] = { ...current, title: value };
      scenarioRows[category] = categoryRows;
      next[scenarioId] = scenarioRows;
      return next;
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${scenarioId}.${category}.${index}`];
      return next;
    });
  };

  const saveChanges = () => {
    const changesToSave = collectEditedItems(rowsByScenario, initialRowsByScenario);
    console.info("[RiskAssessment] Save requested", {
      orgaId,
      editedCount: changesToSave.length,
    });

    const fieldErrors = {};

    displayScenarios.forEach((scenario) => {
      const scenarioId = String(scenario?.id || "");
      const scenarioRows = rowsByScenario?.[scenarioId] || {};

      CATEGORY_ORDER.forEach((category) => {
        const values = Array.isArray(scenarioRows?.[category])
          ? scenarioRows[category]
          : [];
        values.forEach((item, index) => {
          const title = String(item?.title || "").trim();
          if (!title) {
            fieldErrors[`${scenarioId}.${category}.${index}`] =
              "Hypothesis title cannot be empty";
          }
        });
      });
    });

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      console.warn("[RiskAssessment] Save blocked due to validation errors", {
        errorCount: Object.keys(fieldErrors).length,
      });
      alertError("Hypothesis titles cannot be empty.");
      return;
    }

    if (changesToSave.length > 0) {
      console.groupCollapsed(
        `[RiskAssessment] Saving ${changesToSave.length} edited hypothesis item(s)`
      );
      console.table(
        changesToSave.map((change) => ({
          scenarioId: change.scenarioId,
          category: change.category,
          index: change.index,
          before: change.before,
          after: change.after,
        }))
      );
      console.groupEnd();
    } else {
      console.info("[RiskAssessment] No edited items found, nothing changed.");
      return;
    }
    const ops = changesToSave
      .filter((change) => change.usecaseId)
      .map((change) => ({
        op: "edit_title",
        scenario_id: String(change.scenarioId),
        usecase_id: String(change.usecaseId),
        updated_title: String(change.after),
        where: {
          scenario_id: String(change.scenarioId),
          usecase_id: String(change.usecaseId),
        },
        changes: {
          title: String(change.after),
        },
      }));

    if (ops.length === 0) {
      alertError(
        "No editable hypothesis IDs were found. Please refresh and try again."
      );
      return;
    }

    dispatch(updateRiskAssessmentHypotheses(orgaId, ops)).then((res) => {
      if (!res) return;
      setInitialRowsByScenario(JSON.parse(JSON.stringify(rowsByScenario)));
    });
  };

  const beginItemEdit = (scenarioId, category, index, currentTitle) => {
    setEditingItem({ scenarioId, category, index });
    setEditingValue(String(currentTitle || ""));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${scenarioId}.${category}.${index}`];
      return next;
    });
  };

  const cancelItemEdit = () => {
    setEditingItem(null);
    setEditingValue("");
  };

  const handleScenarioChange = (event) => {
    const nextScenarioId = String(event.target.value || "");
    setSelectedScenarioId(nextScenarioId);
    setEditingItem(null);
    setEditingValue("");
  };

  const confirmItemEdit = () => {
    if (!editingItem) return;
    const { scenarioId, category, index } = editingItem;
    if (!String(editingValue || "").trim()) {
      setErrors((prev) => ({
        ...prev,
        [`${scenarioId}.${category}.${index}`]: "Hypothesis title cannot be empty",
      }));
      return;
    }

    updateHypothesisValue(scenarioId, category, index, editingValue);
    cancelItemEdit();
  };

  const isEditing = (scenarioId, category, index) =>
    Boolean(
      editingItem &&
        editingItem.scenarioId === scenarioId &&
        editingItem.category === category &&
        editingItem.index === index
    );

  const getItemKey = (scenarioId, category, index) =>
    `${scenarioId}.${category}.${index}`;

  const shouldEnableShowMore = (itemKey) => Boolean(showMoreByKey[itemKey]);

  const toggleItemExpanded = (itemKey) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  const setTextMeasureRef = (itemKey, node) => {
    if (node) {
      textMeasureRefs.current[itemKey] = node;
    } else {
      delete textMeasureRefs.current[itemKey];
    }
  };

  const CATEGORY_THEME = useMemo(
    () => ({
      worst: {
        headerBg: alpha(theme.palette.error.main, 0.08),
        surfaceBg: theme.palette.background.paper,
        border: alpha(theme.palette.primary.main, 0.18),
        title: theme.palette.error.dark || theme.palette.error.main,
        badgeBg: alpha(theme.palette.error.main, 0.14),
      },
      median: {
        headerBg: alpha(theme.palette.warning.main, 0.13),
        surfaceBg: theme.palette.background.paper,
        border: alpha(theme.palette.primary.main, 0.18),
        title: theme.palette.warning.dark || theme.palette.warning.main,
        badgeBg: alpha(theme.palette.warning.main, 0.24),
      },
      best: {
        headerBg: alpha(theme.palette.success.main, 0.1),
        surfaceBg: theme.palette.background.paper,
        border: alpha(theme.palette.primary.main, 0.18),
        title: theme.palette.success.dark || theme.palette.success.main,
        badgeBg: alpha(theme.palette.success.main, 0.18),
      },
    }),
    [theme]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box>
        {displayScenarios.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
            No selected scenarios found.
          </Paper>
        ) : (
          <>
              <Box
                sx={{
                  mb: 1.2,
                  maxWidth: { xs: "100%", md: 980 },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.75,
                    color: "text.secondary",
                    lineHeight: 2,
                  }}
                >
                  Select a scenario to review the hypotheses across worst-case, median, and best-case outcomes.
                </Typography>
                <TextField
                  select
                  value={activeScenarioId}
                  onChange={handleScenarioChange}
                  label="Scenario title"
                  size="small"
                  fullWidth
                  sx={{
                    backgroundColor: alpha(theme.palette.common.white, 0.9),
                    borderRadius: 1.5,
                    "& .MuiSelect-select": {
                      whiteSpace: "normal",
                      lineHeight: 1.35,
                      py: 1.1,
                      pr: 4.2,
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 440,
                          maxWidth: "min(960px, calc(100vw - 24px))",
                          "& .MuiMenuItem-root": {
                            whiteSpace: "normal",
                            lineHeight: 1.35,
                            py: 1,
                            alignItems: "flex-start",
                          },
                        },
                      },
                    },
                  }}
                >
                  {displayScenarios.map((scenario, index) => {
                    const itemId = String(scenario?.id || `scenario-${index + 1}`);
                    const itemTitle =
                      String(scenario?.title || "").trim() || `Scenario ${index + 1}`;
                    return (
                      <MenuItem key={itemId} value={itemId}>
                        {itemTitle}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>
              {[selectedScenario].filter(Boolean).map((scenario) => {
              const scenarioId = String(scenario?.id || "");
              const row = rowsByScenario?.[scenarioId] || {};

              return (
                <Box key={scenarioId} sx={{ mb: 0 }}>
                  <Box>
                    <Box
                      sx={{
                        mb: 1.3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        flexWrap: "wrap",
                        rowGap: 0.8,
                      }}
                    >
                      <Chip
                        size="small"
                        label={`${activeScenarioHypothesesCount} hypotheses`}
                        sx={{
                          height: 24,
                          flexShrink: 0,
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.dark,
                          fontWeight: 700,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.5,
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "repeat(3, minmax(0, 1fr))",
                        },
                        overflowX: "visible",
                        scrollSnapType: "none",
                        pb: { xs: 0.6, md: 0 },
                        WebkitOverflowScrolling: "touch",
                        "&::-webkit-scrollbar": {
                          height: 6,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.25),
                          borderRadius: 999,
                        },
                      }}
                    >
                      {CATEGORY_ORDER.map((category) => {
                        const values = Array.isArray(row?.[category]) ? row[category] : [];
                        const caseTheme = CATEGORY_THEME[category] || CATEGORY_THEME.median;
                        return (
                          <Box
                            key={`${scenarioId}-${category}`}
                            sx={{
                              scrollSnapAlign: "none",
                              scrollSnapStop: "normal",
                            }}
                          >
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 0,
                                backgroundColor: caseTheme.surfaceBg,
                                height: { xs: 360, md: "calc(100vh - 320px)" },
                                borderColor: caseTheme.border,
                                overflow: "hidden",
                                borderRadius: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: caseTheme.headerBg,
                                  border: `1px solid ${caseTheme.border}`,
                                  borderLeft: "none",
                                  borderRight: "none",
                                  borderTop: "none",
                                  minHeight: 42,
                                  px: 1.2,
                                  py: 0.7,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 0.8,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      letterSpacing: 0.3,
                                      color: caseTheme.title,
                                      textAlign: "left",
                                    }}
                                  >
                                    {CATEGORY_LABELS[category]}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={`${values.length}`}
                                    sx={{
                                      height: 22,
                                      fontWeight: 700,
                                      backgroundColor: caseTheme.badgeBg,
                                      color: caseTheme.title,
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  p: 0,
                                  height: { xs: 300, md: "calc(100vh - 362px)" },
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                  "&::-webkit-scrollbar": {
                                    width: 8,
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    backgroundColor: alpha(theme.palette.common.black, 0.04),
                                    borderRadius: 999,
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.28),
                                    borderRadius: 999,
                                  },
                                }}
                              >
                                {values.length === 0 ? (
                                  <Typography
                                    variant="body2"
                                    sx={{ px: 1.2, py: 1.1, color: "text.secondary" }}
                                  >
                                    No hypotheses
                                  </Typography>
                                ) : (
                                  values.map((item, index) => {
                                    const itemKey = getItemKey(scenarioId, category, index);
                                    return activeEditMode ? (
                                      <Box
                                        key={`${scenarioId}-${category}-${index}`}
                                        sx={{
                                          px: 1.2,
                                          py: 0.9,
                                          minHeight: 82,
                                          display: "flex",
                                          alignItems: "flex-start",
                                          "&:hover .hypothesis-edit-btn, &:focus-within .hypothesis-edit-btn":
                                            {
                                              opacity: 1,
                                            },
                                          ...(index < values.length - 1
                                            ? {
                                                borderBottom: `1px solid ${alpha(
                                                  theme.palette.primary.main,
                                                  0.14
                                                )}`,
                                              }
                                            : {}),
                                        }}
                                      >
                                        {isEditing(scenarioId, category, index) ? (
                                          <Box sx={{ width: "100%" }}>
                                            <TextField
                                              value={editingValue}
                                              onChange={(e) => setEditingValue(e.target.value)}
                                              size="small"
                                              fullWidth
                                              multiline
                                              minRows={3}
                                              variant="outlined"
                                              sx={{
                                                width: "100%",
                                                "& .MuiOutlinedInput-root": {
                                                  backgroundColor: "transparent",
                                                },
                                              }}
                                              error={Boolean(errors[`${scenarioId}.${category}.${index}`])}
                                              helperText={
                                                errors[`${scenarioId}.${category}.${index}`] || ""
                                              }
                                            />
                                            <Box
                                              sx={{
                                                mt: 0.6,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                gap: 0.5,
                                              }}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={confirmItemEdit}
                                                aria-label="save"
                                              >
                                                <CheckIcon fontSize="small" />
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                onClick={cancelItemEdit}
                                                aria-label="cancel"
                                              >
                                                <CloseIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              alignItems: "flex-start",
                                              maxWidth: "100%",
                                              gap: 0.35,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                minWidth: 0,
                                              }}
                                            >
                                              <Typography
                                                ref={(node) =>
                                                  setTextMeasureRef(itemKey, node)
                                                }
                                                variant="body2"
                                                sx={{
                                                  lineHeight: 1.55,
                                                  wordBreak: "break-word",
                                                  display: "block",
                                                  overflow: "hidden",
                                                  maxHeight: expandedItems[itemKey]
                                                    ? "none"
                                                    : "4.65em",
                                                }}
                                              >
                                                {item?.title}
                                              </Typography>
                                              {shouldEnableShowMore(itemKey) && (
                                                <Button
                                                  size="small"
                                                  onClick={() => toggleItemExpanded(itemKey)}
                                                  sx={{
                                                    mt: 0.15,
                                                    minWidth: 0,
                                                    p: 0,
                                                    textTransform: "none",
                                                    lineHeight: 1.2,
                                                    fontSize: 12,
                                                  }}
                                                >
                                                  {expandedItems[itemKey] ? "Show less" : "Show more"}
                                                </Button>
                                              )}
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.25,
                                                flexShrink: 0,
                                              }}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  beginItemEdit(
                                                    scenarioId,
                                                    category,
                                                    index,
                                                    item?.title || ""
                                                  )
                                                }
                                                aria-label="edit"
                                                className="hypothesis-edit-btn"
                                                sx={{
                                                  opacity: 0,
                                                  p: 0.25,
                                                  transition: "opacity 0.15s ease-in-out",
                                                }}
                                              >
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    ) : (
                                      <Box
                                        key={`${scenarioId}-${category}-${index}`}
                                        sx={{
                                          px: 1.2,
                                          py: 0.9,
                                          minHeight: 82,
                                          display: "flex",
                                          alignItems: "flex-start",
                                          ...(index < values.length - 1
                                            ? {
                                                borderBottom: `1px solid ${alpha(
                                                  theme.palette.primary.main,
                                                  0.14
                                                )}`,
                                              }
                                            : {}),
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            minWidth: 0,
                                          }}
                                        >
                                          <Typography
                                            ref={(node) =>
                                              setTextMeasureRef(itemKey, node)
                                            }
                                            variant="body2"
                                            sx={{
                                              lineHeight: 1.5,
                                              wordBreak: "break-word",
                                              display: "block",
                                              overflow: "hidden",
                                              maxHeight: expandedItems[itemKey]
                                                ? "none"
                                                : "4.5em",
                                            }}
                                          >
                                            {item?.title}
                                          </Typography>
                                          {shouldEnableShowMore(itemKey) && (
                                            <Button
                                              size="small"
                                              onClick={() => toggleItemExpanded(itemKey)}
                                              sx={{
                                                mt: 0.15,
                                                minWidth: 0,
                                                p: 0,
                                                textTransform: "none",
                                                lineHeight: 1.2,
                                                fontSize: 12,
                                              }}
                                            >
                                              {expandedItems[itemKey] ? "Show less" : "Show more"}
                                            </Button>
                                          )}
                                        </Box>
                                      </Box>
                                    );
                                  })
                                )}
                              </Box>
                            </Paper>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>

      {activeEditMode && displayScenarios.length > 0 && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.8,
          }}
        >
          <Button variant="contained" onClick={saveChanges}>
            Save changes
          </Button>
        </Box>
      )}
    </Box>
  );
}
